/**
 * Template Error Boundary Component
 * 
 * Provides comprehensive error handling for template-related components
 * with proper logging, user-friendly error messages, and recovery options.
 * 
 * Features:
 * - Graceful error handling for template operations
 * - Structured error logging for debugging
 * - User-friendly error messages with recovery actions
 * - Accessibility-compliant error states
 * - Integration with analytics for error tracking
 * 
 * @author AI Assistant
 * @created 2024
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Stack,
  Divider,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Home as HomeIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { trackEvent } from '../../services/analytics/fathom';
import { EditorEvents } from '../../services/analytics/events';

const ErrorContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2),
  textAlign: 'center',
  maxWidth: 600,
  marginLeft: 'auto',
  marginRight: 'auto',
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3]
}));

const ErrorIconContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiSvgIcon-root': {
    fontSize: '4rem',
    color: theme.palette.error.main
  }
}));

const ActionContainer = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(3),
  '& .MuiButton-root': {
    minWidth: 120
  }
}));

const ErrorDetailsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  textAlign: 'left',
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  maxHeight: 200,
  overflow: 'auto'
}));

class TemplateErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      showDetails: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `template_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error with structured data
    this.logError(error, errorInfo);
    
    // Track error in analytics
    this.trackErrorEvent(error, errorInfo);
    
    // Update state with error details
    this.setState({
      error,
      errorInfo
    });
  }

  logError = (error, errorInfo) => {
    const errorData = {
      error_id: this.state.errorId,
      error_message: error.message,
      error_stack: error.stack,
      component_stack: errorInfo.componentStack,
      user_agent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      component_name: this.props.componentName || 'TemplateComponent',
      user_id: this.props.userId || 'anonymous',
      retry_count: this.state.retryCount
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Template Error Boundary');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Error Data:', errorData);
      console.groupEnd();
    }

    // Send to logging service (implement based on your logging solution)
    this.sendErrorToLoggingService(errorData);
  };

  sendErrorToLoggingService = (errorData) => {
    // This would integrate with your logging service
    // For now, we'll use a simple fetch to a logging endpoint
    try {
      fetch('/api/logs/frontend-errors/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(errorData)
      }).catch(err => {
        console.error('Failed to send error to logging service:', err);
      });
    } catch (err) {
      console.error('Error in error logging:', err);
    }
  };

  trackErrorEvent = (error, errorInfo) => {
    try {
      trackEvent(EditorEvents.TEMPLATE_ERROR, {
        error_type: error.name,
        error_message: error.message,
        component_name: this.props.componentName || 'TemplateComponent',
        error_id: this.state.errorId
      });
    } catch (err) {
      console.error('Failed to track error event:', err);
    }
  };

  handleRetry = () => {
    const newRetryCount = this.state.retryCount + 1;
    
    // Track retry attempt
    try {
      trackEvent(EditorEvents.TEMPLATE_ERROR_RETRY, {
        error_id: this.state.errorId,
        retry_count: newRetryCount
      });
    } catch (err) {
      console.error('Failed to track retry event:', err);
    }

    // Reset error state and increment retry count
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      showDetails: false,
      retryCount: newRetryCount
    });

    // Call custom retry handler if provided
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleGoHome = () => {
    try {
      trackEvent(EditorEvents.TEMPLATE_ERROR_GO_HOME, {
        error_id: this.state.errorId
      });
    } catch (err) {
      console.error('Failed to track go home event:', err);
    }

    window.location.href = '/dashboard';
  };

  toggleErrorDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorId, showDetails, retryCount } = this.state;
      const maxRetries = 3;
      const canRetry = retryCount < maxRetries;

      return (
        <Box
          role="alert"
          aria-live="assertive"
          aria-label="Template error occurred"
          sx={{ p: 2, minHeight: 400, display: 'flex', alignItems: 'center' }}
        >
          <ErrorContainer elevation={3}>
            <ErrorIconContainer>
              <ErrorIcon />
            </ErrorIconContainer>

            <Typography 
              variant="h5" 
              component="h1" 
              gutterBottom 
              color="error"
              id="error-title"
            >
              Oops! Something went wrong
            </Typography>

            <Typography 
              variant="body1" 
              color="text.secondary" 
              paragraph
              aria-describedby="error-title"
            >
              {this.props.fallbackMessage || 
               "We're sorry, but there was an unexpected error with the template system. " +
               "This has been automatically reported to our team."}
            </Typography>

            {errorId && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Error ID: <code>{errorId}</code>
                </Typography>
              </Alert>
            )}

            {retryCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Retry attempt {retryCount} of {maxRetries}
                </Typography>
              </Alert>
            )}

            <ActionContainer direction="row" spacing={2} justifyContent="center">
              {canRetry && (
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRetry}
                  aria-label="Retry the operation"
                >
                  Try Again
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={this.handleGoHome}
                aria-label="Go to dashboard"
              >
                Go to Dashboard
              </Button>
            </ActionContainer>

            {/* Error Details Toggle */}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="text"
                size="small"
                startIcon={<BugReportIcon />}
                endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={this.toggleErrorDetails}
                aria-expanded={showDetails}
                aria-controls="error-details"
              >
                {showDetails ? 'Hide' : 'Show'} Error Details
              </Button>

              <Collapse in={showDetails}>
                <ErrorDetailsContainer id="error-details">
                  <Typography variant="subtitle2" gutterBottom>
                    Error Message:
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {error?.message || 'Unknown error'}
                  </Typography>

                  {process.env.NODE_ENV === 'development' && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Stack Trace:
                      </Typography>
                      <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                        {error?.stack}
                      </Typography>

                      {errorInfo?.componentStack && (
                        <>
                          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                            Component Stack:
                          </Typography>
                          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                            {errorInfo.componentStack}
                          </Typography>
                        </>
                      )}
                    </>
                  )}
                </ErrorDetailsContainer>
              </Collapse>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary">
              If this problem persists, please contact support with the error ID above.
            </Typography>
          </ErrorContainer>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default TemplateErrorBoundary; 